import { useEffect, useState } from 'react';
import { api, clearToken, getToken, type User } from './api';

export type AuthStatus = 'unknown' | 'signed-in' | 'signed-out';

// Module-level cache so route changes (which unmount/remount Shell) can
// initialize the hook synchronously with the last-known user — no flash
// while api.me() re-verifies in the background.
let cachedUser: User | null = null;

export function useCurrentUser(): { status: AuthStatus; user: User | null } {
  const [status, setStatus] = useState<AuthStatus>(() => {
    if (!getToken()) return 'signed-out';
    return cachedUser ? 'signed-in' : 'unknown';
  });
  const [user, setUser] = useState<User | null>(() => cachedUser);

  useEffect(() => {
    let cancelled = false;

    const check = () => {
      const token = getToken();
      if (!token) {
        cachedUser = null;
        setUser(null);
        setStatus('signed-out');
        return;
      }
      api.me()
        .then(me => {
          if (cancelled) return;
          cachedUser = me;
          setUser(me);
          setStatus('signed-in');
        })
        .catch(err => {
          if (cancelled) return;
          const httpStatus = (err as { status?: number }).status;
          if (httpStatus === 401 || httpStatus === 403) clearToken();
          cachedUser = null;
          setUser(null);
          setStatus('signed-out');
        });
    };

    check();

    // Re-check when the extension's content script syncs a token in (e.g.
    // user installed the extension while signed in elsewhere, or signed in
    // via the popup with this tab open).
    const handler = () => check();
    window.addEventListener('tidygolinks:auth-synced', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('tidygolinks:auth-synced', handler);
    };
  }, []);

  return { status, user };
}
